import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Lock, Trash2, Loader2, AlertTriangle, Shield, Mail, Calendar, Briefcase, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { PROFESSION_OPTIONS } from '../constants/professions';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

function formatDate(iso) {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '-'; }
}

export default function AccountSettingsPage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPw, setChangingPw] = useState(false);

  const [editingProfession, setEditingProfession] = useState(false);
  const [professionValue, setProfessionValue] = useState('');
  const [customProfession, setCustomProfession] = useState('');
  const [savingProfession, setSavingProfession] = useState(false);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const isGoogleUser = user?.auth_provider === 'google';

  const startEditProfession = () => {
    const current = user?.profession || '';
    if (PROFESSION_OPTIONS.includes(current)) {
      setProfessionValue(current);
      setCustomProfession('');
    } else if (current) {
      setProfessionValue('Other');
      setCustomProfession(current);
    } else {
      setProfessionValue('');
      setCustomProfession('');
    }
    setEditingProfession(true);
  };

  const saveProfession = async () => {
    const final = professionValue === 'Other' ? customProfession.trim() : professionValue;
    if (!final) { toast.error('Please select a profession'); return; }
    if (professionValue === 'Other' && !customProfession.trim()) { toast.error('Please specify your profession'); return; }
    setSavingProfession(true);
    try {
      await axios.put(`${API_URL}/api/auth/update-profile`, { profession: final }, { withCredentials: true });
      toast.success('Profession updated');
      setEditingProfession(false);
      if (refreshUser) await refreshUser();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to update profession';
      console.error('Profession update error:', err.response?.status, msg);
      toast.error(msg);
    } finally {
      setSavingProfession(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setChangingPw(true);
    try {
      await axios.put(`${API_URL}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      }, { withCredentials: true });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to change password';
      toast.error(msg);
    } finally {
      setChangingPw(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/auth/delete-account`, { withCredentials: true });
      toast.success('Account deleted');
      await logout();
      navigate('/');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to delete account';
      toast.error(msg);
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050507] pt-24 pb-16 px-4" data-testid="account-settings-page">
      <div className="max-w-2xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF4500]">Account</span>
          <h1 className="font-clash text-3xl sm:text-4xl font-medium text-white mt-1">Settings</h1>
        </div>

        {/* Account Info */}
        <div className="bg-[#0c0c10] border border-white/[0.06] rounded-2xl p-6" data-testid="account-info-card">
          <h2 className="text-base font-semibold text-white mb-4">Account Information</h2>
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <Mail className="w-4 h-4 text-white/30 shrink-0" />
              <span className="text-white/50">Email</span>
              <span className="text-white ml-auto">{user?.email}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Shield className="w-4 h-4 text-white/30 shrink-0" />
              <span className="text-white/50">Plan</span>
              <span className="text-white ml-auto capitalize">{user?.plan || 'free'}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Calendar className="w-4 h-4 text-white/30 shrink-0" />
              <span className="text-white/50">Joined</span>
              <span className="text-white ml-auto">{formatDate(user?.created_at)}</span>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <Briefcase className="w-4 h-4 text-white/30 shrink-0 mt-0.5" />
              <span className="text-white/50 shrink-0">Profession</span>
              {editingProfession ? (
                <div className="ml-auto flex flex-col items-end gap-2 w-56">
                  <select
                    value={professionValue}
                    onChange={(e) => { setProfessionValue(e.target.value); if (e.target.value !== 'Other') setCustomProfession(''); }}
                    className="w-full h-8 px-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-white text-xs appearance-none"
                    data-testid="edit-profession-select"
                  >
                    <option value="" disabled className="bg-[#121216]">Select...</option>
                    {PROFESSION_OPTIONS.map(p => (
                      <option key={p} value={p} className="bg-[#121216]">{p}</option>
                    ))}
                  </select>
                  {professionValue === 'Other' && (
                    <Input
                      value={customProfession}
                      onChange={(e) => setCustomProfession(e.target.value)}
                      placeholder="Specify profession"
                      className="h-8 text-xs bg-white/[0.03] border-white/[0.08] text-white"
                      data-testid="edit-custom-profession"
                    />
                  )}
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => setEditingProfession(false)} className="h-7 px-2 text-white/40 hover:text-white" data-testid="cancel-profession-btn">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" onClick={saveProfession} disabled={savingProfession} className="h-7 px-3 bg-[#FF4500] hover:bg-[#CC3700] text-white text-xs" data-testid="save-profession-btn">
                      {savingProfession ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={startEditProfession}
                  className="text-white ml-auto hover:text-[#FF4500] transition-colors"
                  data-testid="edit-profession-btn"
                >
                  {user?.profession || <span className="text-white/25 italic">Not set</span>}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-[#0c0c10] border border-white/[0.06] rounded-2xl p-6" data-testid="change-password-card">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-4 h-4 text-white/50" />
            <h2 className="text-base font-semibold text-white">Change Password</h2>
          </div>
          {isGoogleUser ? (
            <p className="text-sm text-white/40">
              Your account uses Google sign-in. Password management is handled by Google.
            </p>
          ) : (
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label className="text-white/60 text-xs">Current Password</Label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  className="mt-1.5 bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/20 focus:border-[#FF4500]/40"
                  data-testid="current-password-input"
                />
              </div>
              <div>
                <Label className="text-white/60 text-xs">New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  className="mt-1.5 bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/20 focus:border-[#FF4500]/40"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <Label className="text-white/60 text-xs">Confirm New Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="mt-1.5 bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/20 focus:border-[#FF4500]/40"
                  data-testid="confirm-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={changingPw || !currentPassword || !newPassword || !confirmPassword}
                className="bg-[#FF4500] hover:bg-[#CC3700] text-white h-10 px-6 rounded-xl"
                data-testid="change-password-btn"
              >
                {changingPw ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Change Password
              </Button>
            </form>
          )}
        </div>

        {/* Danger Zone */}
        {user?.role !== 'admin' && (
          <div className="bg-[#0c0c10] border border-red-500/20 rounded-2xl p-6" data-testid="danger-zone-card">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <h2 className="text-base font-semibold text-red-400">Danger Zone</h2>
            </div>
            <p className="text-sm text-white/40 mb-4">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(true)}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 gap-2 rounded-xl"
              data-testid="delete-account-btn"
            >
              <Trash2 className="w-4 h-4" />
              Delete Account
            </Button>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="bg-[#121216] border-white/10 sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                Delete Account
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-sm text-white/60">
                This will permanently delete your account and all your data including invoices, emails, responses, and reviews. This cannot be undone.
              </p>
              <div>
                <Label className="text-white/60 text-xs">
                  Type <span className="text-red-400 font-mono">DELETE</span> to confirm
                </Label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="mt-1.5 bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/20 focus:border-red-500/40"
                  data-testid="delete-confirm-input"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="ghost"
                onClick={() => { setShowDeleteDialog(false); setDeleteConfirmText(''); }}
                className="text-white/50 hover:text-white"
                data-testid="delete-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
                className="bg-red-600 hover:bg-red-700 text-white gap-2 rounded-xl"
                data-testid="delete-confirm-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete Permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
